import { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Box,
  Button,
  Tabs,
  Tab,
  Divider,
  Skeleton,
} from "@mui/material";

import { MY_PERF_SCROLL_LIST_PB } from "../../Dashboard/layouts";

import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

function DeltaCell({ value }) {
  const isUp = value >= 0;
  const Icon = isUp ? ArrowUpwardIcon : ArrowDownwardIcon;
  const color = isUp ? "#00A63E" : "#BE3018";

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{ minWidth: 56 }}
    >
      <Typography sx={{ fontSize: "1rem", fontWeight: 700, color }}>
        {isUp ? `+${value}` : `${value}`}
      </Typography>
      <Icon sx={{ fontSize: 16, color }} />
    </Stack>
  );
}

function HCPGrowersRowSkeleton() {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{ pb: 1.5 }}
    >
      <Skeleton variant="rounded" width={56} height={22} sx={{ flexShrink: 0 }} />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          borderLeft: "1px solid #EDEDED",
          pl: 2,
          ml: "0 !important",
        }}
      >
        <Skeleton variant="text" width="72%" height={20} sx={{ maxWidth: 280 }} />
        <Skeleton variant="text" width="40%" height={18} sx={{ maxWidth: 160, mt: 0.25 }} />
      </Box>
      <Skeleton variant="rounded" width={64} height={28} sx={{ flexShrink: 0 }} />
    </Stack>
  );
}

function HCPGrowersContentSkeleton() {
  return (
    <Box
      sx={{
        flex: "1 1 0",
        minHeight: 0,
        maxHeight: 400,
        overflowY: "auto",
        pr: 1,
      }}
    >
      <Stack spacing={2} sx={{ pb: MY_PERF_SCROLL_LIST_PB }}>
        {[0, 1, 2, 3, 4].map((idx) => (
          <Box key={idx}>
            <HCPGrowersRowSkeleton />
            {idx < 4 && <Divider sx={{ borderColor: "#EDEDED" }} />}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

export default function HCPGrowersCard({
  title = "HCP Growers / Decliners in Last 3 Months (Top HCPs)",
  increaseData = [],
  decreaseData = [],
  isLoading = false,
  onExplore, // (hcp) => void
}) {
  const [tab, setTab] = useState(0);

  const rows = useMemo(
    () => (tab === 0 ? increaseData : decreaseData),
    [tab, increaseData, decreaseData],
  );

  const hasRows = rows.length > 0;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "8px",
        border: "0.5px solid #F0F0F0",
        boxShadow: "1px 4px 4px 0px #00000008",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <CardContent
        sx={{
          p: "24px",
          pb: "0 !important",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1 }}>
            {title}
          </Typography>

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="standard"
            sx={{
              minHeight: 32,
              mb: 1.5,
              "& .MuiTab-root": {
                textTransform: "none",
                minHeight: 32,
                padding: 0,
                mr: 2,
                fontSize: 13,
                fontWeight: 600,
                color: "#2D2D2D",
              },
              "& .MuiTab-root.Mui-selected": {
                color: "#D96B04",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#D96B04",
                height: 2,
              },
            }}
          >
            <Tab label="Highest Increase in Starts" />
            <Tab label="Highest Decrease in Starts" />
          </Tabs>
        </Box>

        {/* Content area: non-scroll for empty, scroll only if rows exist */}
        {isLoading ? (
          <HCPGrowersContentSkeleton />
        ) : !hasRows ? (
          <Box
            sx={{
              flex: "1 1 0",
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
            }}
          >
            <Typography sx={{ fontSize: 14, color: "#718096" }}>
              No data found
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              flex: "1 1 0",
              minHeight: 0,
              maxHeight: 400,
              overflowY: "auto",
              pr: 1,
              msOverflowStyle: "none",
            }}
          >
            <Stack
              spacing={2}
              sx={{
                pb: MY_PERF_SCROLL_LIST_PB,
              }}
            >
              {rows.map((r, idx) => (
                <Box key={r.id}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ pb: 1.5 }}
                  >
                    {/* Left: delta */}
                    <DeltaCell value={r.delta} />

                    {/* Middle: name + specialty */}
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        borderLeft: "1px solid #EDEDED",
                        pl: 2,
                        ml: "0 !important",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#2D2D2D",
                          lineHeight: 1.2,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {r.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: "#718096",
                          mt: "2px",
                          lineHeight: 1.2,
                        }}
                      >
                        {r.specialty}
                      </Typography>
                    </Box>

                    {/* Right: Explore */}
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => onExplore?.(r)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 13,
                        minWidth: "auto",
                        px: 1,
                        color: "#D96B04",
                        flexShrink: 0,
                      }}
                    >
                      Explore
                    </Button>
                  </Stack>

                  {idx !== rows.length - 1 && (
                    <Divider sx={{ borderColor: "#EDEDED" }} />
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
